import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from './Icon.tsx';
import { panelStyle, usePopover } from './usePopover.ts';
import { useI18n } from '~/i18n/index.tsx';
import { initials } from '~/lib/initials.ts';
import { useSession } from '~/lib/session.tsx';

/**
 * Who is signed in, and the way out.
 *
 * One chip at the end of the command bar — the initials, the name over the role — that opens a
 * short menu: the account as the panel knows it, then Sign out. Signing out used to be a bare
 * glyph after the name, the one control in the bar with no word on it, beside a name that did
 * nothing when pressed.
 *
 * The account's details sit above the menu rather than in it: a menu holds things to do, and a
 * name is not one of them.
 */
export function AccountMenu() {
  const { t } = useI18n();
  const { profile, roles, signOut } = useSession();
  const { open, setOpen, close, pos, rootRef, panelRef, id } = usePopover({ align: 'end' });

  // Opening puts the caret on the first action, so Enter signs out and Escape backs away.
  useEffect(() => {
    if (open && pos) {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [open, pos, panelRef]);

  const name = profile?.fullName || profile?.email;
  const role = roles.includes('SuperAdmin') ? t('role.SuperAdmin') : t('role.Admin');
  const mark = initials(profile?.fullName, profile?.email);

  return (
    <div className="menu" ref={rootRef}>
      <button
        type="button"
        className="account"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        <span className="sr-only">{t('nav.account')} </span>
        <span className="avatar avatar--solid account__avatar" aria-hidden="true">
          {mark}
        </span>
        <span className="account__text">
          <bdi className="account__name">{name}</bdi>
          <span className="account__role">{role}</span>
        </span>
        <Icon name="chevronDown" size={14} className="account__caret" />
      </button>

      {open
        ? createPortal(
            <div ref={panelRef} className="menu__panel account-menu" style={panelStyle(pos)}>
              <div className="account-menu__who">
                <span className="avatar avatar--solid account-menu__avatar" aria-hidden="true">
                  {mark}
                </span>
                <span className="account-menu__text">
                  <bdi className="account-menu__name">{name}</bdi>
                  {profile?.email ? <bdi className="account-menu__mail">{profile.email}</bdi> : null}
                  <span className="account-menu__role">{role}</span>
                </span>
              </div>

              <div
                id={id}
                role="menu"
                aria-label={t('nav.account')}
                onKeyDown={(event) => {
                  // A menu is one stop in the tab order; leaving it closes it.
                  if (event.key === 'Tab') {
                    close();
                  }
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="menu__item menu__item--danger"
                  onClick={() => {
                    close();
                    void signOut();
                  }}
                >
                  <Icon name="signOut" className="menu__item-icon" size={18} />
                  <span>{t('auth.signOut')}</span>
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
